import { eventChannel } from 'redux-saga';
import { call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { selectTyped, takeEvery } from '@pressreader/appstore';
import { error, warn } from '@pressreader/logger';
import { ViewName } from '@pressreader/navigation-types';

import {
    getParams,
    historyBack,
    historyForward,
    historyGo,
    pushState,
    replaceState,
    replaceStateWithReload,
    subscribeToTransition,
    unsubscribeFromTransition,
    viewer,
} from '../api';
import { NavigationParams, ReplaceType, Update } from '../types';
import { isStaleNavigation } from '../utils';

import {
    backHistoryAction,
    forwardHistoryAction,
    goHistoryAction,
    goViewAction,
    goViewDone,
    navConfigUpdated,
    navigateDone,
    navigateFailed,
    navigateToAction,
    navigateToPreviousViewAction,
    pushStateToHistoryAction,
    refocusConfigsAddAction,
    refocusConfigsRemoveAction,
    refocusTargetsAddAction,
    replaceStateHistoryAction,
    replaceStateWithReloadHistoryAction,
    resetStateHistoryAction,
    updateLocationAction,
    updateStateHistoryAction,
} from './actions';
import { getRefocusConfigurationTopLevelElement, getRefocusTargetTopLevelElement, goTo } from './sagas.utils';
import { selectCurrentNavId, selectLocation, selectNavConfigSuppressRefocus, selectNavigationHistoryState, selectPrevNavConfig } from './selectors';

function createTransitionChannel() {
    return eventChannel<Update>(emit => {
        // Subscribe and unsubscribe MUST use the same function reference:
        // createSubscribers.remove() matches by identity. The previous code
        // subscribed a fresh `update => emit(update)` closure but unsubscribed
        // `emit`, so the handler was never removed and the channel's teardown was
        // a silent no-op (the transition subscriber leaked for the page lifetime).
        const handler = (update: Update) => emit(update);
        subscribeToTransition(handler);

        return () => unsubscribeFromTransition(handler);
    });
}

function* watchOnTransitions() {
    const channel: ReturnType<typeof createTransitionChannel> = yield call(createTransitionChannel);

    while (true) {
        try {
            const payload: Update = yield take(channel);

            yield put(updateLocationAction(payload));
        } catch (err) {
            error(err);
        }
    }
}

function* goView({ payload }: ReturnType<typeof goViewAction>) {
    const { viewName, replace, isForceReplace, ignoreIssueInfo } = payload;

    const params: NavigationParams = { ...payload.params, viewName };

    if (!ignoreIssueInfo) {
        const { state }: ReturnType<typeof selectNavigationHistoryState> = yield select(selectNavigationHistoryState);
        if ('currentIssueInfo' in state) {
            params['currentIssueInfo'] = state['currentIssueInfo'];
        }
    }

    yield put(resetStateHistoryAction());

    const replaceType = !replace ? ReplaceType.Off : isForceReplace ? ReplaceType.ForceOn : ReplaceType.On;

    const { isSuccess } = yield* goTo(params, replaceType);

    if (!isSuccess) {
        return;
    }
    yield put(goViewDone(payload));
}

function* updateStateHistory({ payload }: ReturnType<typeof updateStateHistoryAction>) {
    const { params, url, reload = false } = payload;

    const location: ReturnType<typeof selectLocation> = yield select(selectLocation);
    const { state, viewName }: ReturnType<typeof selectNavigationHistoryState> = yield select(selectNavigationHistoryState);

    const to = url || location.href;
    const name = viewer?.getActiveViewName() || viewName;
    const allParams = name ? getParams(name, params) : { stateParams: {} };
    const stateParams = { ...state, ...allParams.stateParams };

    if (reload) {
        yield put(replaceStateWithReloadHistoryAction({ to, state: stateParams }));
    } else {
        yield put(replaceStateHistoryAction({ to, state: stateParams }));
    }
}

function* resetStateHistory() {
    let activeView = viewer?.getActiveView();

    if (!activeView) {
        const { viewName }: ReturnType<typeof selectNavigationHistoryState> = yield select(selectNavigationHistoryState);

        activeView = viewer?.findView(viewName);
    }

    if (activeView) {
        activeView.resetState?.();
        const params = activeView.getResetParams?.();

        if (params) {
            yield put(updateStateHistoryAction({ params }));
        }
    }
}

function* pushStateToHistory(action: ReturnType<typeof pushStateToHistoryAction>) {
    const {
        payload: { to, state },
    } = action;

    yield call(pushState, to, state);
}

function* replaceStateHistory(action: ReturnType<typeof replaceStateHistoryAction>) {
    const {
        payload: { to, state },
    } = action;

    yield call(replaceState, to, state);
}

function* replaceStateHistoryWithReload(action: ReturnType<typeof replaceStateWithReloadHistoryAction>) {
    const {
        payload: { to, state },
    } = action;

    yield call(replaceStateWithReload, to, state);
}

function* goHistory(action: ReturnType<typeof goHistoryAction>) {
    const { payload } = action;

    yield call(historyGo, payload);
}

function* backHistory() {
    yield call(historyBack);
}

function* forwardHistory() {
    yield call(historyForward);
}

function* addOrRemoveRefocusConfigurationDefault() {
    const isSuppressRefocus: ReturnType<typeof selectNavConfigSuppressRefocus> = yield select(selectNavConfigSuppressRefocus);
    const configuration = getRefocusConfigurationTopLevelElement();

    if (isSuppressRefocus) {
        yield put(refocusConfigsRemoveAction({ reason: 'Suppress refocus', config: configuration }));
        return;
    }
    yield put(refocusConfigsAddAction(configuration));
}

function* addRefocusTargetDefault() {
    yield put(refocusTargetsAddAction(getRefocusTargetTopLevelElement()));
}

function* handleNavigateToFailed({ payload }: ReturnType<typeof navigateFailed>) {
    const { error: err, config } = payload;

    const currentNavId = yield* selectTyped(selectCurrentNavId);
    if (isStaleNavigation(config, currentNavId)) {
        // A failure of a superseded navigation must not yank the user off the view that
        // already replaced it (e.g. the article not-available page, #174564). Log it as a
        // warning only — an error-level log here would raise false alerts for a benign outcome.
        warn(`Navigation to view "${config.viewName}" failed after being superseded; skipping the Error view redirect.`, err);
        return;
    }

    error(err);
    error(new Error(`Navigation to view "${config.viewName}" failed. ${err ? `Error: "${err}"` : ''}`));
    yield put(navigateToAction({ viewName: ViewName.Error }));
}

function* navigateToPreviousView({
    payload: { useOpenHomeViewFallback: goHomeIfNoPrevious = true, useGoBackInHistoryFallback: goBackIfNoPrevious = false },
}: ReturnType<typeof navigateToPreviousViewAction>) {
    const prevNavConfig = yield* selectTyped(selectPrevNavConfig);

    if (prevNavConfig) {
        const navigageToPayload = {
            viewName: prevNavConfig.viewName,
            params: {
                ...prevNavConfig,
            },
        };
        return void (yield put(navigateToAction(navigageToPayload)));
    }

    warn('No previous navigation action payload found, navigating to Home view as fallback.');
    if (goBackIfNoPrevious) {
        return void (yield put(backHistoryAction()));
    }
    if (goHomeIfNoPrevious) {
        return void (yield put(navigateToAction({ viewName: ViewName.Home })));
    }
    warn('No previous view in history to navigate back to.');
}

function* saga() {
    yield fork(watchOnTransitions);

    yield takeEvery(goViewAction, goView);
    yield takeEvery(resetStateHistoryAction, resetStateHistory);
    yield takeEvery(updateStateHistoryAction, updateStateHistory);
    yield takeEvery(pushStateToHistoryAction, pushStateToHistory);
    yield takeEvery(replaceStateHistoryAction, replaceStateHistory);
    yield takeEvery(replaceStateWithReloadHistoryAction, replaceStateHistoryWithReload);
    yield takeEvery(goHistoryAction, goHistory);
    yield takeEvery(backHistoryAction, backHistory);
    yield takeEvery(forwardHistoryAction, forwardHistory);

    yield takeLatest(navigateDone, addOrRemoveRefocusConfigurationDefault);
    yield takeLatest([navigateDone, navConfigUpdated], addRefocusTargetDefault);
    yield takeEvery(navigateFailed, handleNavigateToFailed);

    yield takeLatest(navigateToPreviousViewAction, navigateToPreviousView);
}

export { handleNavigateToFailed, saga };
